import Joi from "joi";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import SearchSelectComponent from "../../components/SearchSelectComponent";
import { SelectItemsProps } from "../../components/Select2Component";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { SearchByClientName } from "../../models/Client/SearchByClientName";
import { EditProjectDTO } from "../../models/Project/EditProjectDTO";
import { ProjectDTO } from "../../models/Project/ProjectDTO";
import { AssistanceService } from "../../services/AssistanceService";
import { ClientService } from "../../services/ClientService";
import { ProjectService } from "../../services/ProjectService";
import { ProjectNavBar } from "../../components/Projects/ProjectNavBar";
import AssistancesFromProject from "../../components/Assistances/AssistanceFromProject";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";


export default function ProjectEdit() {
    const { id } = useParams<{ id: string }>();
    const intl = useIntl();

    const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
    //construtor com valores default para prevenir erro visto na console ao carregar dados na página web
    const [project, setProject] = useState<ProjectDTO>({
        id: null,
        erpId: '',
        description: '',
        startDate: null,
        endDate: null,
        budgetSellingPrice: null,
        budgetCostPrice: null,
        notes: '',
        client: {
            id: 0,
            erpId: '',
            vatNumber: '',
            name: '',
            billingCode: '',
            billingCodeDebtTime: '',
            phoneNumber: '',
            email: '',
            paymentConditions: '',
            website: '',
            nameCountry: '',
            address: '',
            address2: '',
            locality: '',
            postalCode: '',
            countryId: 0,
        },
    });
    const [clientSelected, setclientSelected] = useState<SelectItemsProps | null>(null);

    const projectService = new ProjectService();
    const clientService = new ClientService();

    //RENTABILIDADE
    const [isActiveProfitabilityModule, setIsActiveProfitabilityModule] = useState<boolean>(false);
    const assistanceService = new AssistanceService();

    const schemaProjectValidation = Joi.object({
        description: Joi.string()
            .required()
            .messages({
                "any.required": intl.formatMessage({
                    id: "Screens.Projects.Validation.DescriptionRequired",
                }),
                "string.base": intl.formatMessage({
                    id: "Screens.Projects.Validation.DescriptionRequired",
                }),
                "string.empty": intl.formatMessage({
                    id: "Screens.Projects.Validation.DescriptionRequired",
                }),
            }),
        client: Joi.string()
            .required()
            .messages({
                "any.required": intl.formatMessage({
                    id: "Screens.Projects.Validation.ClientRequired",
                }),
                "string.base": intl.formatMessage({
                    id: "Screens.Projects.Validation.ClientRequired",
                }),
                "string.empty": intl.formatMessage({
                    id: "Screens.Projects.Validation.ClientRequired",
                }),
            }),
    });

    const validateProject = (project: any, erpId: String) => {
        return new Promise(function (resolve) {
            var validationResult = schemaProjectValidation.validate(
                {
                    description: project.description,
                    client: erpId,
                },
                { abortEarly: true }
            );
            resolve(validationResult);

            if (validationResult.error != null) {
                var message = validationResult.error!.message;
                Toast.Show("error", message);
                return false;
            }
        });
    };

    useEffect(() => {
        const getProject = async () => {
            var response = await projectService.GetByIdErp(String(id));

            if (response.success === false || response.obj == null) {
                Toast.Show("error", response.message);
                return;
            }

            setProject(response.obj);

            setclientSelected({
                value: response.obj.client?.erpId!,
                label: response.obj.client?.name!,
            });
        };

        getProject();
    }, []);

    const getClientByName = async (value: string): Promise<SelectItemsProps[]> => {
        var data: SearchByClientName = {
            name: value,
        };
        var response = await clientService.SearchByName(data);
        if (response.success === false || response.obj === null) {
            Toast.Show("error", response.message);
            return [];
        }

        const list: SelectItemsProps[] = [];

        response.obj.forEach((element: any) => {
            var item: SelectItemsProps = {
                value: element.erpId,
                label: element.name,
            };
            list.push(item);
        });

        return list;
    };

    const editProject = async () => {
        validateProject(project, project.client.erpId).then(async (result: any) => {
            if (result.error != null) {
                setLoadingUpdate(false);
            } else {
                const updatedProject: EditProjectDTO = {
                    erpId: project.erpId,
                    description: project.description,
                    startDate: project.startDate || new Date(),
                    budgetSellingPrice: project.budgetSellingPrice,
                    budgetCostPrice: project.budgetCostPrice,
                    notes: project.notes,
                    clientId: clientSelected?.value!,
                    // Usei o operador de coalescência nula
                    // parecido ao "??" mas "||" aceita valores (0, "", undefined, false,.. e não apenas "" e undefined )
                };

                setLoadingUpdate(true);
                const response = await projectService.Edit(updatedProject);
                setLoadingUpdate(false);

                if (response.success !== true) {
                    Toast.Show("error", response.message);
                    return;
                }
                Toast.Show("success", response.message);
            }
        });
    };

    //rentabilidade
    //obter valor setting (módulo rentabilidade)
    const { availableModules } = useAvailableModulesContext();
    useEffect(() => {
        if (availableModules.includes("ProfitabilitySage50cIsActiveProfitabilityModule")) {
          setIsActiveProfitabilityModule(true);
        }
      }, [availableModules]);

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <Row style={{ margin: "0px", padding: "0px" }}>
                        <Col style={{ margin: "0px", padding: "0px" }}>
                            <PageTitle
                                title={intl.formatMessage({ id: 'Screens.ProjectEdit.Title' })}
                                dynamicTitle={() => `Obra - ${project?.description}`}
                                allowGoBack={true}
                            />
                        </Col>
                    </Row>
                </div>

                <div className="pageBody">
                    <Row style={{ margin: "0px", padding: "0px", alignItems: "flex-start", alignContent: "flex-start", display: "flex", flexDirection: "row"}}>
                        <Col xl={isActiveProfitabilityModule ? 6 : 6}>
                            <label style={{ margin: "13px 0", padding: "0px" }}>
                                <b>
                                    <FormattedMessage id="Screens.ProjectEdit.SubTitle2" />
                                </b>
                            </label>

                            <Row>
                                <SearchSelectComponent
                                    xl={12}
                                    lg={12}
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    label={intl.formatMessage({
                                        id: "Screens.Projects.Info.Client",
                                    })}
                                    loadOptions={getClientByName}
                                    onChange={(e) => {
                                        setclientSelected(e);
                                    }}
                                    value={clientSelected}
                                    style={{ width: "100%" }}
                                />
                            </Row>

                            <label style={{ margin: "13px 0px", padding: "0px" }}>
                                <b>
                                    <FormattedMessage id="Screens.ProjectEdit.SubTitle" />
                                </b>
                            </label>
                            <Row>
                                <Row style={{ margin: "0px", padding: "0px" }}>
                                    <Row style={{ marginBottom: "10px" }}>
                                        <InputComponent
                                            xl={12}
                                            lg={12}
                                            md={12}
                                            sm={12}
                                            xs={12}
                                            style={{ width: "100%" }}
                                            type={"text"}
                                            value={project.description}
                                            label={intl.formatMessage({
                                                id: "Screens.Projects.Info.Description",
                                            })}
                                            readOnly={false}
                                            onChange={(t: string) => {
                                                setProject({ ...project!, description: t });
                                            }}
                                        />
                                    </Row>
                                    <Row style={{ marginBottom: "10px" }}>
                                        <InputComponent
                                            xl={3}
                                            lg={3}
                                            md={3}
                                            sm={12}
                                            xs={12}
                                            style={{ width: "100%" }}
                                            type={"number"}
                                            value={project.budgetSellingPrice!}
                                            label={intl.formatMessage({
                                                id: "Screens.Projects.Info.BudgetSellingPrice",
                                            })}
                                            readOnly={false}
                                            onChange={(t: number) => {
                                                setProject({ ...project!, budgetSellingPrice: t });
                                            }}
                                        />
                                        <InputComponent
                                            xl={3}
                                            lg={3}
                                            md={3}
                                            sm={12}
                                            xs={12}
                                            style={{ width: "100%" }}
                                            type={"number"}
                                            value={project.budgetCostPrice!}
                                            label={intl.formatMessage({
                                                id: "Screens.Projects.Info.BudgetCostPrice",
                                            })}
                                            readOnly={false}
                                            onChange={(t: number) => {
                                                setProject({ ...project!, budgetCostPrice: t });
                                            }}
                                        />
                                        <InputComponent
                                            xl={3}
                                            lg={3}
                                            md={3}
                                            sm={12}
                                            xs={12}
                                            style={{ width: "100%" }}
                                            type={"date"}
                                            value={project.startDate ? Utilities.formatDateYYYYMMDD(new Date(project.startDate)) : ""}
                                            label={intl.formatMessage({
                                                id: "Screens.Projects.Info.StartDate",
                                            })}
                                            readOnly={false}
                                            onChange={(t: string) => {
                                                const parsedDate = new Date(t);
                                                setProject({ ...project!, startDate: parsedDate });
                                            }}
                                        />
                                    </Row>
                                    <Row style={{ marginBottom: "10px" }}>
                                        <InputComponent
                                            xl={12}
                                            lg={12}
                                            md={12}
                                            sm={12}
                                            xs={12}
                                            style={{ width: "100%" }}
                                            type={"text"}
                                            value={project.notes!}
                                            label={intl.formatMessage({
                                                id: "Screens.Projects.Info.Notes",
                                            })}
                                            readOnly={false}
                                            onChange={(t: string) => {
                                                setProject({ ...project!, notes: t });
                                            }}
                                        />
                                    </Row>
                                    <Row style={{ margin: "0px", marginTop: "10px", padding: "0px" }}>
                                        <Col xl={2} lg={2} md={2} sm={12} xs={12} style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}>
                                            <ButtonComponent
                                                disabled={loadingUpdate}
                                                loading={loadingUpdate}
                                                text={intl.formatMessage({ id: "ProjectEdit.Save" })}
                                                onClick={editProject}
                                            />
                                        </Col>
                                    </Row>
                                </Row>
                            </Row>
                        </Col>
                        
                                    
                        {/* Mostrar navbar apenas se modulo da rentabilidade estiver ativo */}
                        
                            <Col xl={6}>
                                <ProjectNavBar
                                    project={project}
                                    reloadExecutions={false}
                                    loadingUpdate={!loadingUpdate}
                                    profitabilityModule={isActiveProfitabilityModule}
                                />
                            </Col>
                        
                    </Row>
                </div>
            </div>
        </Layout >
    );
}